import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { green } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';
import styles from './MissingInvoicesTab.module.css';
import {
    getMissingInvoices,
    getMissingInvoicesCsv,
    getMissingInvoicesJson,
    getMissingInvoicesTaskId,
    startMissingInvoicesTask,
} from '../../../../../Services/Search.services';
import { FieldKeys, MissingInvoicesTable } from './MissingInvoicesTable';
import { toast } from 'react-toastify';
import { datetimeToFormattedString } from '../../../../../Utils/Utils';
import MissingInvoicesTableFilters from './MissingInvoicesTableFilters';
import { GroupByControl } from './GroupByControl';

const groupByFolderHiddenColumns = [
    FieldKeys.reference,
    FieldKeys.message,
    FieldKeys.messageId,
    FieldKeys.purchaseId,
    FieldKeys.documentId,
    FieldKeys.orderedAt,
    FieldKeys.transactionId,
    FieldKeys.oType,
];

const defaultHiddenColumns = [
    FieldKeys.lastOrderedAt,
];

export function MissingInvoicesTab() {
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [missingInvoices, setMissingInvoices] = useState([]);
    const [filteredMissingInvoices, setFilteredMissingInvoices] = useState([]);
    const [isSuccess, setIsSuccess] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [groupByState, setGroupByState] = useState({ isGroupBy: false, groupByOption: null });
    const groupByControlRef = useRef();

    const customHiddenColumns = useMemo(() => {
        return groupByState.isGroupBy ? groupByFolderHiddenColumns : defaultHiddenColumns;
    }, [groupByState.isGroupBy]);

    const pollMissingInvoicesEndpoint = useCallback((taskId) => {
        const pollingDelayMs = 1000;
        const groupBy = groupByState.isGroupBy ? groupByState.groupByOption : null;

        getMissingInvoices(taskId, groupBy)
            .then((response) => {
                if (response.status === 200) {
                    setMissingInvoices(response.data?.['missingInvoices'] || []);
                    setIsLoading(false);
                } else {
                    setTimeout(() => pollMissingInvoicesEndpoint(taskId), pollingDelayMs);
                }
            })
            .catch((error) => {
                if (error.response) {
                    if (error.response.status === 404) {
                        toast.error('Missing Invoices not found');
                        setMissingInvoices([]);

                        return;
                    }
                }

                toast.error('Oops, something went wrong');
                setIsLoading(false);
            });
    }, [groupByState]);

    const loadMissingInvoicesTaskId = useCallback(async () => {
        const { longTaskId: taskId, startedAt } = await getMissingInvoicesTaskId()
            .catch((error) => {
                if (error.response) {
                    if (error.response.status === 404) {
                        void generateMissingInvoices();
                    }
                }
            });

        if (taskId) {
            const dateTimeString = datetimeToFormattedString(startedAt, null);

            setTaskId(taskId);
            setLastUpdate(dateTimeString);
            pollMissingInvoicesEndpoint(taskId);
        }
    }, [pollMissingInvoicesEndpoint]);

    const generateMissingInvoices = async () => {
        const isSuccess = await startMissingInvoicesTask();
        setIsSuccess(!!isSuccess);

        if (isSuccess) {
            void loadMissingInvoicesTaskId();
        } else {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadMissingInvoicesTaskId();
    }, [loadMissingInvoicesTaskId]);

    const handleRefreshClick = async () => {
        setIsLoading(true);
        await generateMissingInvoices();
    };

    const handleFilterChange = (filteredInvoices) => {
        setFilteredMissingInvoices(filteredInvoices);
    };

    const handleGroupByChange = (groupByData) => {
        setGroupByState(groupByData);

        if (taskId) {
            setIsLoading(true);
        }
    };

    const handleResetFilters = () => {
        if (groupByControlRef.current) {
            groupByControlRef.current.reset();
        }

        setGroupByState({ isGroupBy: false, groupByOption: null });

        if (taskId) {
            setIsLoading(true);
            pollMissingInvoicesEndpoint(taskId, null);
        }
    };

    const handleDownloadButtonClick = () => {
        setIsDownloading(true);

        if (groupByState.isGroupBy) {
            getMissingInvoicesJson(taskId, groupByState.groupByOption)
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([JSON.stringify(response.data)], { type: 'application/json' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Missing Invoices - ${lastUpdate}.json`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    toast.error('Error downloading file');
                    console.error(error);
                })
                .finally(() => {
                    setIsDownloading(false);
                });
        } else {
            getMissingInvoicesCsv(taskId, null)
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Missing Invoices - ${lastUpdate}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    toast.error('Error downloading file');
                    console.error(error);
                })
                .finally(() => {
                    setIsDownloading(false);
                });
        }
    };

    return (
        <div>
            <div className={styles.buttonTimeLabelContainer}>
                <div className={styles.controlsContainer}>
                    <div className={styles.buttonWrapper}>
                        <Button
                            variant="contained"
                            className={styles.reloadButton}
                            sx={isSuccess && {
                                bgcolor: green[500],
                                '&:hover': {
                                    bgcolor: green[700],
                                },
                            }}
                            disabled={isLoading}
                            onClick={handleRefreshClick}
                        >
                            Refresh Missing Invoices
                        </Button>
                        {isLoading && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: green[500],
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                }}
                            />
                        )}
                    </div>
                    <div className={styles.buttonWrapper}>
                        <Button
                            className={styles.downloadButton}
                            variant="outlined"
                            disabled={isLoading || !taskId || isDownloading}
                            onClick={handleDownloadButtonClick}
                        >
                            <div className={styles.downloadButtonContent}>
                                Download Invoices
                                <span className={styles.downloadButtonSubtitle}>Since Jan 1st (ignores filters)</span>
                            </div>
                        </Button>
                        {isDownloading && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: green[500],
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                }}
                            />
                        )}
                    </div>
                </div>
                {
                    !!lastUpdate && (
                        <div className={styles.lastUpdateTimeLabel}>
                            <p>{`Last update: ${lastUpdate}`}</p>
                        </div>
                    )
                }
            </div>
            {lastUpdate && (
                <div className={styles.tableFilters}>
                    {missingInvoices.length > 0 && (
                        <div className={styles.dataControls}>
                            <MissingInvoicesTableFilters
                                invoices={missingInvoices}
                                onFilterChange={handleFilterChange}
                            />
                            <GroupByControl
                                ref={groupByControlRef}
                                onGroupByChange={handleGroupByChange}
                                initialIsGroupBy={groupByState.isGroupBy}
                                initialGroupByOption={groupByState.groupByOption || 'folder'}
                            />
                        </div>
                    )}
                    <div className={styles.table}>
                        <MissingInvoicesTable
                            isLoading={isLoading}
                            invoices={filteredMissingInvoices}
                            isGroupByEnabled={groupByState.isGroupBy}
                            customHiddenColumns={customHiddenColumns}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
